import { useRef, useState, useCallback, memo, useEffect } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import {
  Eye, EyeOff, Lock, Unlock, Plus, Trash2, Diamond, ChevronDown, ChevronRight
} from 'lucide-react';

const FRAME_WIDTH = 8;
const TRACK_HEIGHT = 28;
const RULER_HEIGHT = 24;
const LABEL_WIDTH = 140;

const LayerTrack = memo(function LayerTrack({ layer, isSelected, onSelect, objects, selectedObjectId, currentFrame, totalFrames, onSelectObject, dispatch }) {
  const [expanded, setExpanded] = useState(true);

  const handleKeyframeAreaClick = (e, objectId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.round(x / FRAME_WIDTH);
    if (frame >= 0 && frame <= totalFrames) {
      dispatch({ type: 'SET_FRAME', payload: frame });
      onSelectObject(objectId);
    }
  };

  const trackWidth = totalFrames * FRAME_WIDTH;

  return (
    <div data-testid={`timeline-layer-${layer.id}`}>
      {/* Layer header row */}
      <div
        className={`flex items-center border-b border-white/[0.03] cursor-pointer transition-colors ${
          isSelected ? 'bg-rose-500/[0.06]' : 'hover:bg-white/[0.02]'
        }`}
        style={{ height: TRACK_HEIGHT }}
        onClick={() => onSelect(layer.id)}
      >
        <div className="flex items-center gap-1 px-2 shrink-0 sticky left-0 z-[2] bg-[hsl(220,14%,10%)]" style={{ width: LABEL_WIDTH }}>
          <button
            className="p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          <span className="text-[10px] font-mono truncate flex-1">{layer.name}</span>
          <button
            className="p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: 'TOGGLE_LAYER_VISIBILITY', payload: layer.id });
            }}
            data-testid={`layer-visibility-${layer.id}`}
          >
            {layer.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </button>
          <button
            className="p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: 'TOGGLE_LAYER_LOCK', payload: layer.id });
            }}
            data-testid={`layer-lock-${layer.id}`}
          >
            {layer.locked ? <Lock className="w-2.5 h-2.5" /> : <Unlock className="w-2.5 h-2.5" />}
          </button>
        </div>
        <div className="h-full border-l border-border bg-muted/5" style={{ minWidth: trackWidth }} />
      </div>

      {/* Object rows */}
      {expanded && objects.map(obj => (
        <div
          key={obj.id}
          className={`flex items-center border-b border-white/[0.02] transition-colors ${
            selectedObjectId === obj.id ? 'bg-rose-500/[0.04]' : 'hover:bg-white/[0.015]'
          }`}
          style={{ height: TRACK_HEIGHT }}
        >
          <div
            className="flex items-center gap-1 px-2 pl-6 shrink-0 cursor-pointer sticky left-0 z-[2] bg-[hsl(220,14%,10%)]"
            style={{ width: LABEL_WIDTH }}
            onClick={() => onSelectObject(obj.id)}
          >
            <div
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: obj.fill || '#666' }}
            />
            <span className="text-[10px] font-mono truncate text-muted-foreground">
              {obj.name}
            </span>
          </div>

          <div
            className="h-full border-l border-border/50 relative cursor-crosshair"
            style={{ minWidth: trackWidth }}
            onClick={(e) => handleKeyframeAreaClick(e, obj.id)}
          >
            {obj.keyframes?.map((kf, idx) => (
              <div
                key={idx}
                className="absolute top-1/2 -translate-y-1/2 cursor-pointer z-10 group"
                style={{ left: `${kf.frame * FRAME_WIDTH - 4}px` }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch({ type: 'SET_FRAME', payload: kf.frame });
                  onSelectObject(obj.id);
                }}
                data-testid={`keyframe-${obj.id}-${kf.frame}`}
              >
                <Diamond
                  className={`w-[9px] h-[9px] fill-current ${
                    selectedObjectId === obj.id ? 'text-amber-400' : 'text-amber-600'
                  } group-hover:text-amber-300`}
                />
              </div>
            ))}
            <div
              className="absolute top-0 w-px h-full bg-indigo-500/40 pointer-events-none"
              style={{ left: `${currentFrame * FRAME_WIDTH}px` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
});

export function Timeline() {
  const { state, dispatch } = useAnimation();
  const { t } = useI18n();
  const scrollRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleFrameClick = useCallback((frame) => {
    dispatch({ type: 'SET_FRAME', payload: frame });
  }, [dispatch]);

  const handleSelectObject = useCallback((objectId) => {
    dispatch({ type: 'SELECT_OBJECT', payload: objectId });
  }, [dispatch]);

  const addLayer = () => dispatch({ type: 'ADD_LAYER' });
  const removeLayer = () => {
    if (state.selectedLayerId && state.project.layers.length > 1) {
      dispatch({ type: 'REMOVE_LAYER', payload: state.selectedLayerId });
    }
  };

  const handleScroll = useCallback((e) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  // Mouse wheel -> horizontal scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Drag to scroll
  const isDraggingTimeline = useRef(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });

  const handleTimelineMouseDown = useCallback((e) => {
    // Only drag on middle-click or when clicking on empty track area (not on buttons/keyframes)
    if (e.button === 1 || (e.button === 0 && e.target === scrollRef.current)) {
      isDraggingTimeline.current = true;
      dragStart.current = { x: e.clientX, scrollLeft: scrollRef.current.scrollLeft };
      e.preventDefault();
    }
  }, []);

  const handleTimelineMouseMove = useCallback((e) => {
    if (!isDraggingTimeline.current) return;
    const dx = e.clientX - dragStart.current.x;
    scrollRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
  }, []);

  const handleTimelineMouseUp = useCallback(() => {
    isDraggingTimeline.current = false;
  }, []);

  // Auto-scroll to follow playhead
  useEffect(() => {
    if (!state.isPlaying || !scrollRef.current) return;
    const playheadX = state.currentFrame * FRAME_WIDTH;
    const container = scrollRef.current;
    const visibleStart = container.scrollLeft + LABEL_WIDTH;
    const visibleEnd = container.scrollLeft + container.clientWidth;
    if (playheadX > visibleEnd - 50 || playheadX < visibleStart) {
      container.scrollLeft = Math.max(0, playheadX - container.clientWidth / 2);
    }
  }, [state.currentFrame, state.isPlaying]);

  const trackWidth = state.project.totalFrames * FRAME_WIDTH;

  const handleRulerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frame = Math.round(x / FRAME_WIDTH);
    handleFrameClick(Math.max(0, Math.min(state.project.totalFrames, frame)));
  };

  // Ruler markers
  const step = state.project.fps >= 24 ? 12 : 6;
  const markers = [];
  for (let i = 0; i <= state.project.totalFrames; i += step) {
    markers.push(
      <div
        key={i}
        className="absolute top-0 h-full flex flex-col items-center"
        style={{ left: `${i * FRAME_WIDTH}px` }}
      >
        <span className="text-[9px] font-mono text-muted-foreground leading-none mt-0.5">
          {(i / state.project.fps).toFixed(1)}s
        </span>
        <div className="w-px h-2 bg-border mt-auto" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="timeline-panel">
      {/* Timeline toolbar */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/[0.04] shrink-0">
        <span className="text-[9px] font-display font-semibold text-muted-foreground/50 uppercase tracking-[0.15em] mr-2">{t('timeline')}</span>
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded text-muted-foreground/40 hover:text-foreground" onClick={addLayer} data-testid="add-layer-button">
          <Plus className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded text-muted-foreground/40 hover:text-red-400" onClick={removeLayer} data-testid="remove-layer-button">
          <Trash2 className="w-3 h-3" />
        </Button>
        <div className="flex-1" />
        <span className="text-[9px] font-mono text-muted-foreground/40 tabular-nums">
          {state.currentFrame}f / {(state.currentFrame / state.project.fps).toFixed(2)}s
        </span>
      </div>

      {/* Scrollable timeline content */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-x-auto overflow-y-auto"
        onScroll={handleScroll}
        onMouseDown={handleTimelineMouseDown}
        onMouseMove={handleTimelineMouseMove}
        onMouseUp={handleTimelineMouseUp}
        onMouseLeave={handleTimelineMouseUp}
        style={{ cursor: isDraggingTimeline.current ? 'grabbing' : 'default' }}
        data-testid="timeline-scroll-container"
      >
        {/* Ruler row */}
        <div className="flex sticky top-0 z-[3] border-b border-border bg-[hsl(220,14%,10%)]">
          <div className="shrink-0 sticky left-0 z-[4] bg-[hsl(220,14%,10%)]" style={{ width: LABEL_WIDTH }} />
          <div
            className="relative cursor-pointer select-none shrink-0"
            style={{ height: RULER_HEIGHT, width: trackWidth }}
            onClick={handleRulerClick}
            data-testid="timeline-ruler"
          >
            {markers}
            <div
              className="absolute top-0 w-0.5 h-full bg-indigo-500 z-10 pointer-events-none"
              style={{ left: `${state.currentFrame * FRAME_WIDTH}px` }}
            />
            <div
              className="absolute w-3 h-3 bg-indigo-500 z-10 pointer-events-none"
              style={{
                left: `${state.currentFrame * FRAME_WIDTH - 5}px`,
                top: '-1px',
                clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
              }}
            />
          </div>
        </div>

        {/* Layer tracks */}
        {[...state.project.layers].reverse().map(layer => (
          <LayerTrack
            key={layer.id}
            layer={layer}
            isSelected={state.selectedLayerId === layer.id}
            onSelect={(id) => dispatch({ type: 'SELECT_LAYER', payload: id })}
            objects={layer.objects}
            selectedObjectId={state.selectedObjectId}
            currentFrame={state.currentFrame}
            totalFrames={state.project.totalFrames}
            onSelectObject={handleSelectObject}
            dispatch={dispatch}
          />
        ))}
      </div>
    </div>
  );
}
